// Currency conversion service using real exchange rates
import exchangeRateService from './exchangeRateService';

// Cache for exchange rates to avoid repeated API calls
let exchangeRatesCache: { [key: string]: number } | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 1000 * 60 * 60; // 1 hour

export interface CurrencyInfo {
  code: string;
  name: string;
  symbol: string;
}

export const SUPPORTED_CURRENCIES: CurrencyInfo[] = [
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' }
];

export const convertCurrency = async (amount: number, fromCurrency: string, toCurrency: string): Promise<number> => {
  if (fromCurrency === toCurrency) {
    return amount;
  }

  try {
    return await exchangeRateService.convertCurrency(amount, fromCurrency, toCurrency);
  } catch (error) {
    console.error('Error converting currency:', error);
    return amount; // Return original amount if conversion fails
  }
};

export const getCurrencySymbol = (currencyCode: string): string => {
  const currency = SUPPORTED_CURRENCIES.find(c => c.code === currencyCode);
  return currency?.symbol || currencyCode;
};

export const getCurrencyName = (currencyCode: string): string => {
  const currency = SUPPORTED_CURRENCIES.find(c => c.code === currencyCode);
  return currency?.name || currencyCode;
};

// Enhanced currency formatting with conversion support
export const formatCurrencyWithConversion = async (
  amount: number, 
  fromCurrency: string, 
  toCurrency: string = 'CAD',
  showOriginal: boolean = false
): Promise<string> => {
  const convertedAmount = await convertCurrency(amount, fromCurrency, toCurrency);
  const formatted = formatCurrency(convertedAmount, toCurrency);
  
  if (showOriginal && fromCurrency !== toCurrency) {
    const originalFormatted = formatCurrency(amount, fromCurrency);
    return `${formatted} (${originalFormatted})`;
  }
  
  return formatted;
};

// Simple currency formatter (moved from formatters.ts for consistency)
export const formatCurrency = (amount: number, currency: string = 'CAD'): string => {
  const formatter = new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formatter.format(amount);
};

// Get exchange rate between two currencies
export const getExchangeRate = async (fromCurrency: string, toCurrency: string): Promise<number> => {
  if (fromCurrency === toCurrency) {
    return 1.0;
  }

  try {
    const rates = await exchangeRateService.getExchangeRates(fromCurrency);
    return rates[toCurrency] || 1.0;
  } catch (error) {
    console.error('Error getting exchange rate:', error);
    return 1.0;
  }
};

// Update exchange rates cache
export const updateExchangeRates = async (): Promise<void> => {
  try {
    // Clear cache to force refresh
    exchangeRatesCache = null;
    cacheTimestamp = 0;
    
    // Fetch fresh rates
    await exchangeRateService.getExchangeRates('CAD');
    console.log('Exchange rates updated');
  } catch (error) {
    console.error('Failed to update exchange rates:', error);
  }
}; 